import React, { useRef, useState } from 'react';
import { ArrowLeft, Share2, MapPin } from 'lucide-react';
import { centerToast } from './CenterToast';

// A lightweight, fully-local "map preview" slide — no external map dependency.
function MapSlide({ label }) {
  return (
    <div className="relative w-full h-full bg-[#eef2f0]">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600"><rect fill="%23eef2f0" width="800" height="600"/><path stroke="%23c9d6d1" stroke-width="2" fill="none" d="M0,120 Q200,70 400,120 T800,120 M0,260 L800,260 M0,420 Q200,470 400,420 T800,420 M140,0 L140,600 M420,0 L420,600 M680,0 L680,600"/></svg>')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <MapPin className="h-9 w-9 text-rose-500 fill-rose-500/20 drop-shadow" />
          <div className="mt-1.5 px-3 py-1 rounded-lg bg-white shadow text-xs font-semibold text-[#0F172A]">
            {label}
          </div>
        </div>
      </div>
      <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground/70 bg-white/70 px-1.5 py-0.5 rounded">
        Map preview
      </span>
    </div>
  );
}

// A lightweight, fully-local "programme" slide for courses/classes.
function ProgramSlide({ items = [] }) {
  return (
    <div className="w-full h-full bg-white px-5 py-4 overflow-y-auto">
      <p className="text-[11px] font-bold text-primary mb-2.5 uppercase tracking-wide">Programme</p>
      <div className="flex flex-col gap-2.5">
        {items.map((it, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <span className="text-[11px] font-bold text-primary w-20 flex-shrink-0 pt-0.5 leading-snug">{it.time}</span>
            <span className="text-xs text-foreground leading-snug">{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DetailImageCarousel({ images = [], extra, tag, tagColor, onBack }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  const slides = [
    ...images.filter(Boolean).map((src) => ({ type: 'image', src })),
    ...(extra ? [extra] : [])
  ];

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setIndex(i);
  };

  const scrollTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setIndex(i);
  };

  return (
    <div className="relative h-60 md:h-full flex-shrink-0 bg-muted md:rounded-2xl overflow-hidden" data-testid="detail-image-carousel">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="h-full w-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
      >
        {slides.map((s, i) => (
          <div key={i} className="w-full h-full flex-shrink-0 snap-center" data-testid={`carousel-slide-${i}`}>
            {s.type === 'image' && <img src={s.src} alt="" className="w-full h-full object-cover" />}
            {s.type === 'map' && <MapSlide label={s.label} />}
            {s.type === 'program' && <ProgramSlide items={s.items} />}
          </div>
        ))}
      </div>

      {/* Top overlay controls */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 pointer-events-none">
        <button
          type="button"
          data-testid="detail-back-button"
          onClick={onBack}
          aria-label="Back"
          className="pointer-events-auto w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-sm"
        >
          <ArrowLeft className="h-4.5 w-4.5 text-[#0F172A]" />
        </button>
        <button
          type="button"
          data-testid="detail-share-button"
          onClick={() => centerToast('Link copied!', '🔗')}
          aria-label="Share"
          className="pointer-events-auto w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-sm"
        >
          <Share2 className="h-4 w-4 text-[#0F172A]" />
        </button>
      </div>

      {tag && (
        <span className={`absolute bottom-3 left-4 px-2.5 py-1 rounded-md text-[11px] font-bold text-white ${tagColor}`}>
          {tag}
        </span>
      )}

      {slides.length > 1 && (
        <div className="absolute bottom-3 right-4 flex items-center gap-1.5" data-testid="carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all ${index === i ? 'w-4 bg-white' : 'w-1.5 bg-white/60'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
